import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react'

const MIN_ZOOM = 1
const MAX_ZOOM = 8
const ZOOM_STEP = 1.12

type Pan = { x: number; y: number }

function clampZoom(z: number) {
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z))
}

export type VideoViewportHandle = {
  toggleFullscreen: () => void
  resetView: () => void
}

export function useVideoViewport() {
  const viewportRef = useRef<HTMLDivElement>(null)
  const [zoom, setZoom] = useState(1)
  const [pan, setPan] = useState<Pan>({ x: 0, y: 0 })
  const [isFullscreen, setIsFullscreen] = useState(false)
  const pointersRef = useRef<Map<number, { x: number; y: number }>>(new Map())
  const pinchRef = useRef<{ dist: number; zoom: number } | null>(null)
  const dragRef = useRef<{ lastX: number; lastY: number } | null>(null)

  const resetView = useCallback(() => {
    setZoom(1)
    setPan({ x: 0, y: 0 })
  }, [])

  const toggleFullscreen = useCallback(() => {
    const el = viewportRef.current
    if (!el) return
    if (document.fullscreenElement === el) {
      void document.exitFullscreen().catch(() => {})
      return
    }
    if (document.fullscreenElement) {
      void document
        .exitFullscreen()
        .then(() => el.requestFullscreen())
        .catch(() => {})
      return
    }
    void el.requestFullscreen().catch(() => {})
  }, [])

  useEffect(() => {
    const onFs = () => {
      const el = viewportRef.current
      setIsFullscreen(!!el && document.fullscreenElement === el)
    }
    document.addEventListener('fullscreenchange', onFs)
    return () => document.removeEventListener('fullscreenchange', onFs)
  }, [])

  useEffect(() => {
    const el = viewportRef.current
    if (!el) return

    const onWheel = (e: WheelEvent) => {
      e.preventDefault()
      const rect = el.getBoundingClientRect()
      const cx = e.clientX - rect.left
      const cy = e.clientY - rect.top
      const factor = e.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP

      setZoom((prev) => {
        const next = clampZoom(prev * factor)
        if (next === prev) return prev
        if (next === 1) {
          setPan({ x: 0, y: 0 })
          return next
        }
        const ratio = next / prev
        setPan((p) => ({
          x: cx - (cx - p.x) * ratio,
          y: cy - (cy - p.y) * ratio,
        }))
        return next
      })
    }

    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [])

  const onPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      const el = viewportRef.current
      if (!el) return
      el.setPointerCapture(e.pointerId)
      pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY })

      if (pointersRef.current.size === 2) {
        const pts = [...pointersRef.current.values()]
        const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y)
        pinchRef.current = { dist, zoom }
        dragRef.current = null
        return
      }

      if (zoom > 1) {
        dragRef.current = { lastX: e.clientX, lastY: e.clientY }
      }
    },
    [zoom],
  )

  const onPointerMove = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (!pointersRef.current.has(e.pointerId)) return
      pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY })

      if (pointersRef.current.size === 2 && pinchRef.current) {
        const pts = [...pointersRef.current.values()]
        const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y)
        if (dist < 1 || pinchRef.current.dist < 1) return
        const next = clampZoom(pinchRef.current.zoom * (dist / pinchRef.current.dist))
        setZoom(next)
        if (next === 1) setPan({ x: 0, y: 0 })
        return
      }

      if (dragRef.current && zoom > 1) {
        const dx = e.clientX - dragRef.current.lastX
        const dy = e.clientY - dragRef.current.lastY
        dragRef.current = { lastX: e.clientX, lastY: e.clientY }
        setPan((p) => ({ x: p.x + dx, y: p.y + dy }))
      }
    },
    [zoom],
  )

  const endPointer = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    pointersRef.current.delete(e.pointerId)
    if (pointersRef.current.size < 2) pinchRef.current = null
    if (pointersRef.current.size === 0) dragRef.current = null
  }, [])

  const onDoubleClick = useCallback(() => {
    toggleFullscreen()
  }, [toggleFullscreen])

  const stageStyle = {
    transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
    transformOrigin: '0 0',
  } as const

  return {
    viewportRef,
    zoom,
    isFullscreen,
    isZoomed: zoom > 1.01,
    toggleFullscreen,
    resetView,
    onPointerDown,
    onPointerMove,
    onPointerUp: endPointer,
    onPointerCancel: endPointer,
    onDoubleClick,
    stageStyle,
  }
}
